import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";

interface ThemeToggleProps {
  className?: string;
  /** Visual size variant */
  size?: "sm" | "md";
  /** When true, shows label next to the icon (used in settings rows). */
  withLabel?: boolean;
}

/**
 * Single-button light/dark toggle.
 * — Click to flip immediately.
 * — Persists via ThemeContext (localStorage) and respects prefers-color-scheme on first load.
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = "",
  size = "md",
  withLabel = false,
}) => {
  const { resolvedTheme, toggle } = useTheme();
  const isDark = resolvedTheme === "dark";

  const sizeCls =
    size === "sm"
      ? "p-1.5 w-8 h-8"
      : "p-2 w-9 h-9";

  return (
    <motion.button
      type="button"
      onClick={toggle}
      whileTap={{ scale: 0.9 }}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      aria-pressed={isDark}
      className={`relative inline-flex items-center justify-center gap-2 rounded-lg
        ${sizeCls}
        text-slate-600 hover:bg-slate-100
        dark:text-ink-200 dark:hover:bg-ink-700
        transition-colors
        ${withLabel ? "px-3 w-auto" : ""}
        ${className}`}
    >
      <span className="relative inline-flex items-center justify-center w-4 h-4">
        <AnimatePresence mode="wait" initial={false}>
          {isDark ? (
            <motion.span
              key="moon"
              initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              exit={{ rotate: 90, opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.18 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <Moon className="w-4 h-4 text-accent-400" />
            </motion.span>
          ) : (
            <motion.span
              key="sun"
              initial={{ rotate: 90, opacity: 0, scale: 0.6 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              exit={{ rotate: -90, opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.18 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <Sun className="w-4 h-4 text-amber-500" />
            </motion.span>
          )}
        </AnimatePresence>
      </span>

      {withLabel && (
        <span className="text-sm font-medium">
          {isDark ? "Dark" : "Light"} mode
        </span>
      )}
    </motion.button>
  );
};

export default ThemeToggle;
